import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { FormattedMessage, useIntl } from 'react-intl';
import svg from '../../svg';
import {
  addToCart,
  CART_RENT_APARTMENT,
  CART_ASO_APARTMENT,
  isInCart,
  removeFromCart,
  checkAllowedPropertiesInCart,
  toggleCustomAlert,
  setCartItemsCookie,
  removeItemFromCartItemsCookie,
  updateCartTotalNumber,
  APARTMENT_APPLICATION_URL,
  RENTAL_APPLICATION_URL,
} from '../../Cart/Cart';

// Services
import {
  capitalizeFirstLetter,
  getMetaDataTableValuesAsoApartment,
  getMetaDataTableValuesRentApartment,
  getAvailableStatusMessages,
  RULE_FOR_EMPTY_VALUE,
  getApartmentExtraInfoMessages,
  isImmediatelyAvailableApartment,
  translateBuildingType,
} from '../services/helpers.services';
import { isBrowser } from '../../Utils/Helpers';

import { messages } from '../services/translations.service';

import ButtonLink from './ButtonLink';
import HeaderImage from './HeaderImage';
import SimpleButton from './SimpleButton';

const ajaxUrl = isBrowser()
  ? // eslint-disable-next-line no-undef
    localizedParams.ajaxUrl
  : '';

const ajaxNonce = isBrowser()
  ? // eslint-disable-next-line no-undef
    localizedParams.ajaxNonce
  : '';

const ApartmentCard = ({ data, searchType }) => {
  const CART = searchType === 'aso' ? CART_ASO_APARTMENT : CART_RENT_APARTMENT;
  const APPLICATION_URL =
    searchType === 'aso'
      ? APARTMENT_APPLICATION_URL?.url
      : RENTAL_APPLICATION_URL?.url;

  // The id used when the added to the cart
  const cartId = parseInt(data?.post_id_default_lang) || data.post_id;
  const [inCart, setInCart] = useState(isInCart(cartId, CART));
  const [metaDataTableValues, setMetaDataTableValues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    'aso' === searchType
      ? setMetaDataTableValues(getMetaDataTableValuesAsoApartment(data))
      : setMetaDataTableValues(getMetaDataTableValuesRentApartment(data));
  }, []);

  const CityAreaComponent = () => {
    return (
      <div key={'city-area-component-' + data.post_id}>
        {data.ap_city && data.ap_district && (
          <h3 className='text-lg font-normal text-green mb-4'>
            {data.ap_city},{' '}
            {capitalizeFirstLetter(data.ap_district.toLowerCase())}
          </h3>
        )}
      </div>
    );
  };

  const VolumesComponent = () => {
    const buildingType = translateBuildingType(data.ap_building_type);
    return (
      <div
        key={'volumes-component-' + data.post_id}
        className='flex justify-between align-center mb-4 text-lg'
      >
        <span className='font-bold whitespace-nowrap'>
          {data.ap_size !== null && (
            <>
              {data.ap_size.replace('.', ',')} m<sup>2</sup>
            </>
          )}
        </span>
        <span className='font-normal text-right'>
          {data.ap_apartment_type}, {buildingType}
        </span>
      </div>
    );
  };

  const shortenEngLabel = (data) => {
    const shortcuts = {
      payment: 'pymt',
      month: 'mo',
    };

    const locale = isBrowser() ? Cookies.get('pll_language') : 'fi';
    const labelKey = data.props.id;

    return messages(locale)[labelKey]?.replace(/:([a-zA-Z]+)/g, (m, i) =>
      i in shortcuts ? shortcuts[i] : m
    );
  };

  const MetaTableComponentRow = (data) => {
    return data
      .filter((obj) => {
        return !(parseInt(obj.value) === 0 || !obj.value);
      })
      .map((metaObj) => {
        return (
          <div key={metaObj.value} className='flex justify-between'>
            {/* Highlight these special labels eg. "Varallisuusrajat" */}
            {metaObj.value === RULE_FOR_EMPTY_VALUE ? (
              <strong className='mt-4 block'>{metaObj.label}</strong>
            ) : (
              <span>{shortenEngLabel(metaObj.label)}</span>
            )}
            {/* the RULE_FOR_EMPTY_VALUE means that we only want to show the label without the corresponding value. */}
            {metaObj.value !== RULE_FOR_EMPTY_VALUE && (
              <span>{metaObj.value}</span>
            )}
          </div>
        );
      });
  };

  const MetaTableComponent = () => {
    return (
      <div
        key={'meta-table-component-' + data.post_id}
        className='align-center mb-9 text-lg'
      >
        {MetaTableComponentRow(metaDataTableValues)}
        {extraInfoComponent()}
      </div>
    );
  };

  const extraInfoComponent = () => {
    const message = getApartmentExtraInfoMessages(data, searchType);
    if (message) {
      return (
        <div className='mt-4'>
          <span>{message}</span>
        </div>
      );
    }
    return false;
  };

  let imageData = null;
  if (data.ap_image) {
    imageData = {
      url: JSON.parse(data.ap_image)?.url,
      hash: JSON.parse(data.ap_image)?.hash,
    };
  } else if (data.unit_image) {
    imageData = {
      url: JSON.parse(data.unit_image)?.url,
      hash: JSON.parse(data.unit_image)?.hash,
    };
  }

  const availableText = getAvailableStatusMessages(data);

  const addApartmentToCart = () => {
    addToCart(cartId, CART);

    $.ajax({
      type: 'POST',
      url: ajaxUrl,
      data: {
        action: 'sibling_apartments',
        apartment_id: cartId,
        security: ajaxNonce,
      },
      success: function (data) {
        const siblings = JSON.parse(data).map(Number);

        siblings.forEach((sibling) => {
          addToCart(sibling, CART);
        });

        setInCart(true);
        setCartItemsCookie(CART, cartId);

        setIsLoading(true);

        checkAllowedPropertiesInCart(CART)
          .then((response) => response.json())
          .then((response) => {
            if (response.checkResult == false) {
              removeApartmentFromCart();

              toggleCustomAlert(
                response.result.heading,
                response.result.description
              );
            }

            updateCartTotalNumber(CART);
            setIsLoading(false);
            removeItemFromCartItemsCookie(CART, cartId);
          });
      },
      error: function () {
        return false;
      },
    });

    document.dispatchEvent(new CustomEvent('add-apartment-datalayer'));
  };

  const removeApartmentFromCart = () => {
    removeFromCart(cartId, CART);

    $.ajax({
      type: 'POST',
      url: ajaxUrl,
      data: {
        action: 'sibling_apartments',
        apartment_id: cartId,
        security: ajaxNonce,
      },
      success: function (data) {
        const siblings = JSON.parse(data).map(Number);

        siblings.forEach((sibling) => {
          removeFromCart(sibling, CART);

          updateCartTotalNumber(CART);
          setInCart(false);
        });
      },
      error: function () {
        return false;
      },
    });

    document.dispatchEvent(new CustomEvent('remove-apartment-datalayer'));
  };

  const intl = useIntl();
  const label = intl.formatMessage({ id: 'APARTMENT_MORE_LINK_LABEL' })
  const removeLabel = intl.formatMessage({ id: 'APARTMENT_REMOVE_LABEL' })
  const addToApplication = intl.formatMessage({ id: 'APARTMENT_ADD_LABEL' })

  return (
    <article
      className='apartment-card w-full md:w-6/12 xl:w-4/12 mb-11 lg:mb-20 lg:col-span-4 text-left'
    >
      <div className='mx-0 md:mx-gutter bg-grey-100'>
        {imageData ? (
          <HeaderImage
            ap_url={data.ap_url}
            key={imageData.hash}
            url={imageData.url}
            type={searchType}
            availableInfo={availableText}
            isImmediatelyAvailable={isImmediatelyAvailableApartment(data)}
            address={data.ap_address}
          />
        ) : (
          <HeaderImage
            ap_url={data.ap_url}
            key={Math.random().toString(36).substring(2, 7)}
            usePlaceholder={true}
            type={searchType}
            availableInfo={availableText}
            isImmediatelyAvailable={isImmediatelyAvailableApartment(data)}
            address={data.ap_address}
          />
        )}
        <main className='mx-mobile-gutter lg:mx-gutter py-9 pb-14'>
          {CityAreaComponent()}
          <a href={data.ap_url}>
            <h3 className='text-3xl mb-4'>{data.ap_address}</h3>
          </a>
          {VolumesComponent()}
          {MetaTableComponent()}
          <div className='mb-4'>
            <ButtonLink
              key={data.ap_url}
              url={data.ap_url}
              label={label}
              ariaLabel={`${label}: ${data.ap_address}`}
              extraClasses='btn-green-400 btn-internal-link'
              icon={{
                classes: 'fill-white w-5 ml-4 inline-block',
                svg: svg('long-arrow-alt-right-regular'),
              }}
            />
          </div>
          <div>
            {!isLoading && inCart ? (
              <>
                <ButtonLink
                  key={APPLICATION_URL}
                  url={APPLICATION_URL}
                  label={<FormattedMessage id='GO_TO_APPLICATION_LABEL' />}
                  extraClasses='btn-green-25 btn-internal-link mb-4'
                  icon={{
                    classes: 'fill-white w-5 ml-4 inline-block',
                    svg: svg('long-arrow-alt-right-regular'),
                  }}
                />

                <SimpleButton
                  ariaLabel={`${removeLabel}: ${data.ap_address}`}
                  text={<FormattedMessage id='APARTMENT_REMOVE_LABEL' />}
                  icon={{
                    classes: 'fill-white w-5 ml-4 inline-block',
                    svg: svg('times-regular'),
                  }}
                  extraClasses='remove-from-application'
                  onClick={removeApartmentFromCart}
                />
              </>
            ) : (
              <SimpleButton
                showLoader={isLoading}
                text={addToApplication}
                ariaLabel={`${addToApplication}: ${data.ap_address}`}
                icon={{
                  classes: 'fill-white w-5 ml-4 inline-block',
                  svg: svg('plus-light'),
                }}
                onClick={addApartmentToCart}
                extraClasses='btn-more-link add-to-application'
              />
            )}
          </div>
        </main>
      </div>
    </article>
  );
};

export default ApartmentCard;
